import React, { useState, useRef, useCallback, useEffect } from "react";
import * as XLSX from "xlsx";
import axios from "axios";
import { APIConstant } from "services/api-constant";
import { useTheme } from "context/ThemeContext";

/* ═══════════════════════════════════════════════════════
   BULK EMPLOYEE UPLOAD — Multi-step wizard
   Stack: React + SheetJS (xlsx) + HRMS theme
   Steps: Upload → Preview → Configure → Confirm
═══════════════════════════════════════════════════════ */

/* ── Expected columns in the Excel template ── */
const EXPECTED_COLS = [
  { key:"first_name",          label:"First Name",          required:true  },
  { key:"last_name",           label:"Last Name",           required:true  },
  { key:"email",               label:"Email",               required:true  },
  { key:"phone",               label:"Phone",               required:false },
  { key:"department",          label:"Department",          required:false },
  { key:"designation",         label:"Designation",         required:false },
  { key:"date_of_joining",     label:"Date of Joining",     required:false },
  { key:"gender",              label:"Gender",              required:false },
  { key:"pan_number",          label:"PAN Number",          required:false },
  { key:"uan_number",          label:"UAN Number",          required:false },
  { key:"bank_account_number", label:"Bank Account No.",    required:false },
  { key:"bank_ifsc",           label:"Bank IFSC",           required:false },
  { key:"bank_name",           label:"Bank Name",           required:false },
  { key:"ctc",                 label:"Annual CTC",          required:false },
];

/* ── Sample data for the template ── */
const SAMPLE_ROWS = [
  ["Priya","Nair","priya.nair@company.com","9876543210","Engineering","Software Engineer","2024-01-15","Female","ABCDE1234F","100123456789","1234567890","HDFC0001234","HDFC Bank","900000"],
  ["Rahul","Sharma","rahul.sharma@company.com","9876543211","HR","HR Manager","2023-06-01","Male","BCDEA2345G","100234567890","0987654321","ICIC0005678","ICICI Bank","1200000"],
  ["Deepa","Krishnan","deepa.k@company.com","9876543212","Finance","Senior Accountant","2022-03-10","Female","CDEAB3456H","100345678901","1122334455","SBIN0009012","SBI Bank","750000"],
];

/* ── Download sample Excel template ── */
function downloadSampleTemplate() {
  const wb = XLSX.utils.book_new();
  const headers = EXPECTED_COLS.map(c => c.label);
  const ws = XLSX.utils.aoa_to_sheet([headers, ...SAMPLE_ROWS]);

  // Column widths
  ws["!cols"] = headers.map((h, i) => ({ wch: Math.max(h.length + 4, 18) }));

  // Header row style hint (xlsx doesn't style natively but we set types)
  XLSX.utils.book_append_sheet(wb, ws, "Employees");

  // Instructions sheet
  const instWs = XLSX.utils.aoa_to_sheet([
    ["INSTRUCTIONS"],
    [""],
    ["1. Fill in employee data starting from Row 2 in the 'Employees' sheet."],
    ["2. Columns marked * are required: First Name, Last Name, Email."],
    ["3. Date format: YYYY-MM-DD (e.g. 2024-01-15)"],
    ["4. Gender: Male / Female / Other"],
    ["5. Department and Designation names must match exactly what's in your HRMS."],
    ["6. Annual CTC: numbers only, no currency symbol or commas (e.g. 900000, not ₹9,00,000)."],
    ["7. CTC is per employee — leave blank for anyone you'll set up individually later."],
    ["8. Remove these sample rows before uploading."],
    ["9. Save as .xlsx format."],
  ]);
  instWs["!cols"] = [{ wch: 70 }];
  XLSX.utils.book_append_sheet(wb, instWs, "Instructions");

  XLSX.writeFile(wb, "Hirality_Employee_Template.xlsx");
}

/* ── Utility ── */
const normalise = str => (str || "").toString().toLowerCase().replace(/\s+/g,"_").replace(/[^a-z0-9_]/g,"");



/* ══════════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════ */
export default function BulkEmployeeUpload({ onClose, onSuccess }) {
  const { T } = useTheme();
  const [step, setStep]               = useState(1);
  const [dragOver, setDragOver]       = useState(false);
  const [file, setFile]               = useState(null);
  const [rawRows, setRawRows]         = useState([]);     // all parsed rows
  const [colMap, setColMap]           = useState({});     // key → excel header
  const [excelHeaders, setExcelHeaders] = useState([]);
  const [validRows, setValidRows]     = useState([]);
  const [invalidRows, setInvalidRows] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [managers, setManagers]       = useState([]);
  const [deptMap, setDeptMap]         = useState({});     // excel dept name → dept_id (auto-matched only)
  const [desigMap, setDesigMap]       = useState({});     // excel desig name → desig_id (auto-matched only)
  const [defaultMgr, setDefaultMgr]   = useState("");

  // ── Salary Setup ──────────────────────────────────────────────
  // Salary structure is chosen once and applied to everyone who gets a
  // salary. CTC comes from each row's own "Annual CTC" column in the
  // sheet — fallbackCtc only fills in for rows where that cell was blank.
  const [salaryStructures, setSalaryStructures] = useState([]);
  const [salaryForm, setSalaryForm]   = useState({ structure_id:"", fallbackCtc:"" });

  const [importing, setImporting]     = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [results, setResults]         = useState(null);   // { ok, failed, errors }
  const [parseError, setParseError]   = useState("");
  const fileRef = useRef();

  /* Fetch departments, designations, employees (for manager selection), salary structures */
  useEffect(() => {
    Promise.all([
      axios.get(APIConstant.departments).catch(() => ({ data: [] })),
      axios.get(APIConstant.designations).catch(() => ({ data: [] })),
      axios.get(`${APIConstant.employees}?limit=500`).catch(() => ({ data: [] })),
      axios.get(APIConstant.salaryStructures).catch(() => ({ data: [] })),
    ]).then(([d, des, emp, str]) => {
      setDepartments(Array.isArray(d.data) ? d.data : []);
      setDesignations(Array.isArray(des.data) ? des.data : []);
      setManagers(Array.isArray(emp.data) ? emp.data : []);
      setSalaryStructures(Array.isArray(str.data) ? str.data.filter(s => s.is_active) : []);
    });
  }, []);

  /* ── Parse uploaded Excel ── */
  const parseFile = useCallback((f) => {
    setParseError("");
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb   = XLSX.read(e.target.result, { type: "binary", cellDates: true });
        const ws   = wb.Sheets[wb.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json(ws, { defval: "" });
        if (!json.length) { setParseError("The sheet appears to be empty."); return; }

        const headers = Object.keys(json[0]);
        setExcelHeaders(headers);
        setRawRows(json);

        // Auto-map columns by normalising header names
        const autoMap = {};
        EXPECTED_COLS.forEach(({ key }) => {
          const match = headers.find(h =>
            normalise(h) === key ||
            normalise(h) === normalise(key.replace(/_/g," ")) ||
            normalise(h).includes(key.split("_")[0])
          );
          if (match) autoMap[key] = match;
        });
        setColMap(autoMap);
        setStep(2);
      } catch (err) {
        setParseError("Could not read the file. Please use a valid .xlsx or .csv file.");
      }
    };
    reader.readAsBinaryString(f);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault(); setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f) { setFile(f); parseFile(f); }
  }, [parseFile]);

  const handleFileInput = (e) => {
    const f = e.target.files[0];
    if (f) { setFile(f); parseFile(f); }
  };

  /* ── Validate rows after column mapping, auto-match dept/designation ── */
  const applyMapping = useCallback(() => {
    const valid = [], invalid = [];
    rawRows.forEach((row, idx) => {
      const mapped = {};
      EXPECTED_COLS.forEach(({ key }) => {
        mapped[key] = colMap[key] ? (row[colMap[key]] || "").toString().trim() : "";
      });
      const errors = [];
      if (!mapped.first_name) errors.push("Missing First Name");
      if (!mapped.last_name)  errors.push("Missing Last Name");
      if (!mapped.email || !mapped.email.includes("@")) errors.push("Invalid Email");
      if (errors.length) invalid.push({ row: idx + 2, data: mapped, errors });
      else valid.push(mapped);
    });
    setValidRows(valid);
    setInvalidRows(invalid);

    // Auto-match department / designation by exact (case-insensitive) name.
    // No manual override UI — anything that doesn't match is simply left
    // unassigned and can be set individually afterward in Employee Directory.
    const uniqueDepts  = [...new Set(valid.map(r => r.department).filter(Boolean))];
    const uniqueDesigs = [...new Set(valid.map(r => r.designation).filter(Boolean))];
    const dm = {}, dem = {};
    uniqueDepts.forEach(d => {
      const found = departments.find(dep =>
        dep.name?.toLowerCase() === d.toLowerCase() || dep.department_name?.toLowerCase() === d.toLowerCase()
      );
      dm[d] = found ? (found.id || found.department_id) : "";
    });
    uniqueDesigs.forEach(d => {
      const found = designations.find(des =>
        des.name?.toLowerCase() === d.toLowerCase() || des.designation_name?.toLowerCase() === d.toLowerCase()
      );
      dem[d] = found ? (found.id || found.designation_id) : "";
    });
    setDeptMap(dm); setDesigMap(dem);
    setStep(3);
  }, [rawRows, colMap, departments, designations]);

  /* ── Import employees (+ optional bulk salary assignment) ── */
  const runImport = async () => {
    setImporting(true); setImportProgress(0);
    let ok = 0, failed = 0, errors = [];
    const fallbackCtcNum = parseFloat(salaryForm.fallbackCtc);
    const hasFallbackCtc = !isNaN(fallbackCtcNum) && fallbackCtcNum > 0;

    for (let i = 0; i < validRows.length; i++) {
      const row = validRows[i];
      // Per-employee CTC: prefer the sheet's own "Annual CTC" column;
      // only use the bulk fallback if that cell was blank/invalid.
      const rowCtcNum = parseFloat(row.ctc);
      const ctcToUse  = (!isNaN(rowCtcNum) && rowCtcNum > 0) ? rowCtcNum
                       : (hasFallbackCtc ? fallbackCtcNum : null);
      const applySalary = salaryForm.structure_id && ctcToUse;
      const payload = {
        first_name:          row.first_name,
        last_name:           row.last_name,
        email:               row.email,
        phone:               row.phone || null,
        department_id:       deptMap[row.department]  ? parseInt(deptMap[row.department])  : null,
        designation_id:      desigMap[row.designation]? parseInt(desigMap[row.designation]): null,
        manager_id:          defaultMgr ? parseInt(defaultMgr) : null,
        date_of_joining:     row.date_of_joining || null,
        gender:              row.gender || null,
        pan_number:          row.pan_number || null,
        uan_number:          row.uan_number || null,
        bank_account_number: row.bank_account_number || null,
        bank_ifsc:           row.bank_ifsc || null,
        bank_name:           row.bank_name || null,
        is_active:           true,
      };
      try {
        const res = await axios.post(APIConstant.employees, payload);
        ok++;

        // Salary assignment — same structure for everyone, but each
        // employee's own CTC (from their row in the sheet, or the bulk
        // fallback if their cell was blank). A failure here doesn't undo
        // the employee creation; it's logged separately so HR can assign
        // salary manually afterward from Salary Structures.
        if (applySalary && res.data?.id) {
          try {
            await axios.post(APIConstant.salaryAssign, {
              employee_id:    res.data.id,
              structure_id:   parseInt(salaryForm.structure_id),
              ctc:            ctcToUse,
              effective_from: row.date_of_joining || new Date().toISOString().split("T")[0],
            });
          } catch (salErr) {
            errors.push({ row: row.email, error: "Employee created, but salary assignment failed: " + (salErr.response?.data?.detail || salErr.message) });
          }
        }
      } catch (e) {
        failed++;
        const msg = e.response?.data?.detail || e.message || "Failed";
        errors.push({ row: row.email, error: msg });
      }
      setImportProgress(Math.round(((i + 1) / validRows.length) * 100));
    }
    setResults({ ok, failed, errors });
    setImporting(false);
    setStep(5);
    if (ok > 0 && onSuccess) onSuccess(ok);
  };

  /* Unique dept / desig names from valid rows */
  const uniqueDepts  = [...new Set(validRows.map(r => r.department).filter(Boolean))];
  const uniqueDesigs = [...new Set(validRows.map(r => r.designation).filter(Boolean))];
  const deptMatchedCount  = uniqueDepts.filter(d => deptMap[d]).length;
  const desigMatchedCount = uniqueDesigs.filter(d => desigMap[d]).length;

  const selectedStructure = salaryStructures.find(s => String(s.id) === String(salaryForm.structure_id));

  // Rows with a usable numeric CTC straight from the sheet
  const rowCtc = (row) => {
    const n = parseFloat(row.ctc);
    return !isNaN(n) && n > 0 ? n : null;
  };
  const ctcFromSheetCount = validRows.filter(r => rowCtc(r) !== null).length;
  const ctcMissingCount   = validRows.length - ctcFromSheetCount;
  const fallbackCtcNum    = parseFloat(salaryForm.fallbackCtc);
  const hasFallback       = !isNaN(fallbackCtcNum) && fallbackCtcNum > 0;
  const ctcCoveredCount   = ctcFromSheetCount + (hasFallback ? ctcMissingCount : 0);

  /* ════ RENDER ════ */
  return (
    <div style={{
      position:"fixed", inset:0, zIndex:1000,
      background:"rgba(6,16,31,0.92)", backdropFilter:"blur(8px)",
      display:"flex", alignItems:"center", justifyContent:"center",
      padding:"16px", overflowY:"auto",
    }}>
      <div style={{
        width:"100%", maxWidth:860, background:T.card,
        borderRadius:20, border:`1px solid ${T.border}`,
        boxShadow:T.shadow,
        fontFamily:"'Outfit',sans-serif", color:T.text,
        overflow:"hidden",
      }}>

        {/* ── Header ── */}
        <div style={{
          padding:"20px 28px", display:"flex", alignItems:"center",
          justifyContent:"space-between",
          borderBottom:`1px solid ${T.border}`,
          background:`linear-gradient(135deg,${T.cyan}0F,${T.purple}0F)`,
        }}>
          <div>
            <div style={{ fontSize:18, fontWeight:800, color:T.cyan }}>
              📊 Bulk Employee Upload
            </div>
            <div style={{ fontSize:12, color:T.muted, marginTop:2 }}>
              Import employees from Excel · Step {Math.min(step,4)} of 4
            </div>
          </div>
          <button onClick={onClose} style={{
            width:32, height:32, borderRadius:"50%", border:`1px solid ${T.border}`,
            background:"transparent", color:T.muted, cursor:"pointer", fontSize:16,
            display:"flex", alignItems:"center", justifyContent:"center",
          }}>✕</button>
        </div>

        {/* ── Step Indicator ── */}
        <div style={{ display:"flex", padding:"0 28px", background:T.card2 }}>
          {[
            { n:1, label:"Upload" },
            { n:2, label:"Preview" },
            { n:3, label:"Configure" },
            { n:4, label:"Confirm" },
          ].map((s, i) => (
            <React.Fragment key={s.n}>
              <div style={{
                display:"flex", alignItems:"center", gap:8, padding:"14px 0",
                opacity: step >= s.n ? 1 : 0.4, transition:"opacity .3s",
              }}>
                <div style={{
                  width:26, height:26, borderRadius:"50%", fontSize:11, fontWeight:800,
                  display:"flex", alignItems:"center", justifyContent:"center",
                  background: step > s.n ? `${T.green}1F` : step === s.n ? `${T.cyan}1F` : T.toggle_bg,
                  border: `2px solid ${step > s.n ? T.green : step === s.n ? T.cyan : T.border}`,
                  color: step > s.n ? T.green : step === s.n ? T.cyan : T.muted,
                }}>
                  {step > s.n ? "✓" : s.n}
                </div>
                <span style={{ fontSize:12, fontWeight:600, color: step >= s.n ? T.text : T.muted, whiteSpace:"nowrap" }}>
                  {s.label}
                </span>
              </div>
              {i < 3 && (
                <div style={{
                  flex:1, height:2, alignSelf:"center", margin:"0 12px",
                  background: step > s.n ? T.cyan : T.border, borderRadius:1,
                }}/>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* ── Body ── */}
        <div style={{ padding:"28px", maxHeight:"60vh", overflowY:"auto" }}>

          {/* ══ STEP 1: UPLOAD ══ */}
          {step === 1 && (
            <div>
              {/* Download sample */}
              <div style={{
                background:`${T.green}12`, border:`1px solid ${T.green}33`,
                borderRadius:12, padding:"14px 18px", marginBottom:20,
                display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:10,
              }}>
                <div>
                  <div style={{ fontWeight:700, fontSize:14, color:T.green }}>📥 Download Sample Template</div>
                  <div style={{ fontSize:12, color:T.muted, marginTop:2 }}>
                    Pre-filled with the correct columns and sample data
                  </div>
                </div>
                <button onClick={downloadSampleTemplate} style={{
                  padding:"8px 18px", borderRadius:8, border:`1px solid ${T.green}`,
                  background:"transparent", color:T.green, fontWeight:700, fontSize:13,
                  cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap",
                }}>
                  ↓ Download Template
                </button>
              </div>

              {/* Drop zone */}
              <div
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => fileRef.current.click()}
                style={{
                  border:`2px dashed ${dragOver ? T.cyan : T.border}`,
                  borderRadius:16, padding:"48px 24px", textAlign:"center",
                  cursor:"pointer", transition:"all .25s",
                  background: dragOver ? `${T.cyan}0D` : "transparent",
                }}
              >
                <div style={{ fontSize:40, marginBottom:12 }}>📂</div>
                <div style={{ fontSize:16, fontWeight:700, color:T.text, marginBottom:6 }}>
                  Drop your Excel file here
                </div>
                <div style={{ fontSize:13, color:T.muted, marginBottom:16 }}>
                  or click to browse · supports .xlsx, .xls, .csv
                </div>
                <button style={{
                  padding:"9px 22px", borderRadius:8,
                  background:`linear-gradient(135deg,${T.cyan},${T.purple})`,
                  border:"none", color:"#fff", fontWeight:800, fontSize:13,
                  cursor:"pointer", fontFamily:"inherit",
                }}>Choose File</button>
                {file && (
                  <div style={{ marginTop:14, fontSize:13, color:T.green, fontWeight:600 }}>
                    ✓ {file.name}
                  </div>
                )}
              </div>
              <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleFileInput}
                style={{ display:"none" }}/>

              {parseError && (
                <div style={{
                  marginTop:14, padding:"11px 16px", borderRadius:10,
                  background:`${T.red}1A`, border:`1px solid ${T.red}4D`,
                  color:T.red, fontSize:13, fontWeight:600,
                }}>⚠️ {parseError}</div>
              )}

              {/* Column reference */}
              <div style={{ marginTop:20 }}>
                <div style={{ fontSize:12, color:T.muted, marginBottom:8, fontWeight:600, letterSpacing:".06em", textTransform:"uppercase" }}>
                  Expected Columns
                </div>
                <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                  {EXPECTED_COLS.map(c => (
                    <span key={c.key} style={{
                      padding:"3px 10px", borderRadius:100, fontSize:11, fontWeight:600,
                      background: c.required ? `${T.cyan}1F` : T.toggle_bg,
                      color: c.required ? T.cyan : T.muted,
                      border:`1px solid ${c.required ? `${T.cyan}40` : T.border}`,
                    }}>
                      {c.label}{c.required ? " *" : ""}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ══ STEP 2: PREVIEW & COLUMN MAP ══ */}
          {step === 2 && (
            <div>
              <div style={{ display:"flex", gap:12, marginBottom:20, flexWrap:"wrap" }}>
                {[
                  { label:"Total Rows",  val:rawRows.length,  color:T.cyan  },
                  { label:"Columns Found", val:excelHeaders.length, color:T.purple },
                  { label:"File",        val:file?.name,       color:T.muted },
                ].map(s => (
                  <div key={s.label} style={{
                    flex:1, minWidth:120, padding:"12px 16px", borderRadius:10,
                    background:T.card2, border:`1px solid ${T.border}`,
                  }}>
                    <div style={{ fontSize:20, fontWeight:900, color:s.color }}>{s.val}</div>
                    <div style={{ fontSize:11, color:T.muted, marginTop:2 }}>{s.label}</div>
                  </div>
                ))}
              </div>

              {/* Column mapping */}
              <div style={{ marginBottom:20 }}>
                <div style={{ fontSize:13, fontWeight:700, color:T.text, marginBottom:12 }}>
                  🔗 Map Columns
                  <span style={{ fontSize:11, color:T.muted, fontWeight:400, marginLeft:8 }}>
                    Auto-detected — adjust if needed
                  </span>
                </div>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                  {EXPECTED_COLS.map(col => (
                    <div key={col.key} style={{
                      display:"flex", alignItems:"center", gap:10,
                      padding:"8px 12px", borderRadius:8,
                      background:T.card2, border:`1px solid ${T.border}`,
                    }}>
                      <span style={{ fontSize:11, fontWeight:700, minWidth:110, color:col.required?T.cyan:T.text }}>
                        {col.label}{col.required?" *":""}
                      </span>
                      <select value={colMap[col.key] || ""} onChange={e => setColMap(p => ({ ...p, [col.key]:e.target.value }))}
                        style={{
                          flex:1, background:T.inp_bg, border:`1px solid ${T.border}`,
                          borderRadius:6, padding:"4px 8px", color:T.text,
                          fontSize:11, fontFamily:"inherit",
                        }}>
                        <option value="" style={{background:T.opt_bg}}>— skip —</option>
                        {excelHeaders.map(h => <option key={h} value={h} style={{background:T.opt_bg}}>{h}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
              </div>

              {/* Data preview */}
              <div style={{ fontSize:13, fontWeight:700, color:T.text, marginBottom:10 }}>
                👁 Data Preview <span style={{ fontWeight:400, color:T.muted, fontSize:12 }}>(first 5 rows)</span>
              </div>
              <div style={{ overflowX:"auto", borderRadius:10, border:`1px solid ${T.border}` }}>
                <table style={{ width:"100%", borderCollapse:"collapse", fontSize:11 }}>
                  <thead>
                    <tr style={{ background:T.card2 }}>
                      {excelHeaders.map(h => (
                        <th key={h} style={{ padding:"8px 12px", textAlign:"left", color:T.muted,
                          borderBottom:`1px solid ${T.border}`, whiteSpace:"nowrap" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rawRows.slice(0, 5).map((row, i) => (
                      <tr key={i} style={{ borderBottom:`1px solid ${T.border}` }}>
                        {excelHeaders.map(h => (
                          <td key={h} style={{ padding:"7px 12px", color:T.text, whiteSpace:"nowrap", maxWidth:160, overflow:"hidden", textOverflow:"ellipsis" }}>
                            {(row[h] || "").toString()}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ══ STEP 3: CONFIGURE ══ */}
          {step === 3 && (
            <div>
              {/* Validation summary */}
              <div style={{ display:"flex", gap:12, marginBottom:20 }}>
                <div style={{ flex:1, padding:"14px", borderRadius:10, border:`1px solid ${T.green}40`, background:`${T.green}12`, textAlign:"center" }}>
                  <div style={{ fontSize:28, fontWeight:900, color:T.green }}>{validRows.length}</div>
                  <div style={{ fontSize:12, color:T.muted }}>Valid Employees</div>
                </div>
                {invalidRows.length > 0 && (
                  <div style={{ flex:1, padding:"14px", borderRadius:10, border:`1px solid ${T.red}40`, background:`${T.red}12`, textAlign:"center" }}>
                    <div style={{ fontSize:28, fontWeight:900, color:T.red }}>{invalidRows.length}</div>
                    <div style={{ fontSize:12, color:T.muted }}>Skipped (errors)</div>
                  </div>
                )}
              </div>

              {/* Invalid rows detail */}
              {invalidRows.length > 0 && (
                <div style={{ marginBottom:20, padding:"12px 16px", borderRadius:10, background:`${T.red}12`, border:`1px solid ${T.red}33` }}>
                  <div style={{ fontSize:12, fontWeight:700, color:T.red, marginBottom:8 }}>⚠️ Skipped Rows</div>
                  {invalidRows.slice(0,5).map((r,i) => (
                    <div key={i} style={{ fontSize:11, color:T.muted, marginBottom:4 }}>
                      Row {r.row}: {r.errors.join(", ")}
                    </div>
                  ))}
                  {invalidRows.length > 5 && <div style={{ fontSize:11, color:T.muted }}>...and {invalidRows.length-5} more</div>}
                </div>
              )}

              {/* Department / Designation auto-match summary — no manual mapping UI.
                  Anything unmatched is left blank on import; fix individually
                  afterward in Employee Directory if needed. */}
              {(uniqueDepts.length > 0 || uniqueDesigs.length > 0) && (
                <div style={{
                  marginBottom:20, padding:"12px 16px", borderRadius:10,
                  background:`${T.cyan}0D`, border:`1px solid ${T.cyan}26`,
                }}>
                  <div style={{ fontSize:12.5, fontWeight:700, color:T.cyan, marginBottom:6 }}>
                    🏢 Department & Designation — Auto-matched
                  </div>
                  <div style={{ fontSize:12, color:T.muted, lineHeight:1.7 }}>
                    {uniqueDepts.length > 0 && (
                      <div>
                        Departments: <strong style={{ color:deptMatchedCount===uniqueDepts.length?T.green:T.amber }}>
                          {deptMatchedCount} of {uniqueDepts.length} matched
                        </strong>
                      </div>
                    )}
                    {uniqueDesigs.length > 0 && (
                      <div>
                        Designations: <strong style={{ color:desigMatchedCount===uniqueDesigs.length?T.green:T.amber }}>
                          {desigMatchedCount} of {uniqueDesigs.length} matched
                        </strong>
                      </div>
                    )}
                    {(deptMatchedCount < uniqueDepts.length || desigMatchedCount < uniqueDesigs.length) && (
                      <div style={{ marginTop:4, color:T.amber }}>
                        ! Unmatched values are imported without a department/designation — assign them manually afterward from Employee Directory.
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Salary Setup — structure chosen once, CTC comes from each
                  employee's own row in the sheet (not shared across everyone) */}
              <div style={{ marginBottom:20 }}>
                <div style={{ fontSize:13, fontWeight:700, color:T.text, marginBottom:10 }}>
                  💰 Salary Setup
                  <span style={{ fontSize:11, color:T.muted, fontWeight:400, marginLeft:8 }}>
                    Optional — CTC is read per employee from the sheet
                  </span>
                </div>

                {validRows.length > 0 && (
                  <div style={{
                    marginBottom:12, padding:"10px 14px", borderRadius:8,
                    background: ctcFromSheetCount > 0 ? `${T.cyan}0D` : T.toggle_bg,
                    border:`1px solid ${ctcFromSheetCount > 0 ? `${T.cyan}26` : T.border}`,
                    fontSize:12, color:T.muted, lineHeight:1.7,
                  }}>
                    <strong style={{ color:ctcFromSheetCount > 0 ? T.cyan : T.text }}>
                      {ctcFromSheetCount} of {validRows.length}
                    </strong> employees have a CTC value in the sheet.
                    {ctcMissingCount > 0 && <> {ctcMissingCount} left blank{hasFallback ? " will use the fallback below" : " will be skipped unless you set a fallback"}.</>}
                  </div>
                )}

                <div>
                  <label style={{ fontSize:11, color:T.muted, display:"block", marginBottom:5, textTransform:"uppercase", letterSpacing:"0.06em" }}>
                    Salary Structure — applied to every employee that gets a CTC
                  </label>
                  {salaryStructures.length === 0 ? (
                    <div style={{ padding:"10px 14px", background:`${T.amber}0D`, border:`1px solid ${T.amber}33`, borderRadius:8, fontSize:12, color:T.amber }}>
                      No active salary structures. Set one up under Payroll → Salary Structures, or skip and assign later.
                    </div>
                  ) : (
                    <select value={salaryForm.structure_id} onChange={e => setSalaryForm(p => ({ ...p, structure_id:e.target.value }))}
                      style={{
                        width:"100%", background:T.inp_bg, border:`1px solid ${T.border}`,
                        borderRadius:8, padding:"9px 12px", color:T.text, fontSize:13, fontFamily:"inherit",
                      }}>
                      <option value="" style={{background:T.opt_bg}}>— None (assign later) —</option>
                      {salaryStructures.map(s => (
                        <option key={s.id} value={s.id} style={{background:T.opt_bg}}>{s.name}</option>
                      ))}
                    </select>
                  )}
                </div>

                {salaryForm.structure_id && ctcMissingCount > 0 && (
                  <div style={{ marginTop:12 }}>
                    <label style={{ fontSize:11, color:T.muted, display:"block", marginBottom:5, textTransform:"uppercase", letterSpacing:"0.06em" }}>
                      Fallback CTC (₹) — for the {ctcMissingCount} row{ctcMissingCount!==1?"s":""} without one
                    </label>
                    <input type="number" min={0} value={salaryForm.fallbackCtc}
                      onChange={e => setSalaryForm(p => ({ ...p, fallbackCtc:e.target.value }))}
                      placeholder="Leave blank to skip salary for those employees"
                      style={{
                        width:"100%", background:T.inp_bg, border:`1px solid ${T.border}`,
                        borderRadius:8, padding:"9px 12px", color:T.text, fontSize:13,
                        fontFamily:"inherit", boxSizing:"border-box",
                      }}/>
                  </div>
                )}

                {salaryForm.structure_id && ctcCoveredCount > 0 && (
                  <div style={{ marginTop:10, padding:"9px 14px", borderRadius:8, background:`${T.green}0D`, border:`1px solid ${T.green}26`, fontSize:12, color:T.green }}>
                    ✓ <strong>{ctcCoveredCount} of {validRows.length}</strong> employees will be assigned <strong>{selectedStructure?.name}</strong>,
                    each at their own CTC{hasFallback && ctcMissingCount > 0 ? ` (fallback ₹${Number(fallbackCtcNum).toLocaleString("en-IN")}/yr for those without one)` : ""}.
                  </div>
                )}
              </div>

              {/* Default manager */}
              <div style={{ marginBottom:8 }}>
                <div style={{ fontSize:13, fontWeight:700, color:T.text, marginBottom:10 }}>
                  👤 Default Reporting Manager
                  <span style={{ fontSize:11, color:T.muted, fontWeight:400, marginLeft:8 }}>
                    Applied to all employees without a manager specified
                  </span>
                </div>
                <select value={defaultMgr} onChange={e => setDefaultMgr(e.target.value)}
                  style={{
                    width:"100%", background:T.inp_bg, border:`1px solid ${T.border}`,
                    borderRadius:8, padding:"10px 14px", color:T.text, fontSize:13, fontFamily:"inherit",
                  }}>
                  <option value="" style={{background:T.opt_bg}}>— None (assign later) —</option>
                  {managers.map(m => (
                    <option key={m.id} value={m.id} style={{background:T.opt_bg}}>
                      {m.first_name} {m.last_name} — {m.email}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* ══ STEP 4: CONFIRM ══ */}
          {step === 4 && (
            <div style={{ textAlign:"center" }}>
              <div style={{ fontSize:52, marginBottom:16 }}>🚀</div>
              <div style={{ fontSize:20, fontWeight:800, color:T.text, marginBottom:8 }}>
                Ready to Import {validRows.length} Employees
              </div>
              <div style={{ fontSize:13, color:T.muted, marginBottom:28, lineHeight:1.7 }}>
                {deptMatchedCount} of {uniqueDepts.length} departments matched ·{" "}
                {desigMatchedCount} of {uniqueDesigs.length} designations matched
                {defaultMgr && " · Default manager assigned"}
                {salaryForm.structure_id && ctcCoveredCount > 0 && (
                  <> · Salary: <strong style={{ color:T.green }}>{selectedStructure?.name}</strong> for {ctcCoveredCount} of {validRows.length} employees (per-row CTC)</>
                )}
              </div>

              {/* Summary table */}
              <div style={{ textAlign:"left", borderRadius:12, border:`1px solid ${T.border}`, overflow:"hidden", marginBottom:24 }}>
                <div style={{ background:T.card2, padding:"10px 16px", fontSize:11, fontWeight:700, color:T.muted, letterSpacing:".06em", textTransform:"uppercase" }}>
                  Preview (first 5 employees)
                </div>
                {validRows.slice(0,5).map((r,i) => (
                  <div key={i} style={{
                    display:"flex", alignItems:"center", gap:12,
                    padding:"10px 16px", borderBottom:`1px solid ${T.border}`,
                  }}>
                    <div style={{ width:32, height:32, borderRadius:"50%",
                      background:`linear-gradient(135deg,${T.cyan},${T.purple})`,
                      display:"flex", alignItems:"center", justifyContent:"center",
                      fontSize:12, fontWeight:800, color:"#fff", flexShrink:0 }}>
                      {r.first_name[0]}{r.last_name[0]}
                    </div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:13, fontWeight:700 }}>{r.first_name} {r.last_name}</div>
                      <div style={{ fontSize:11, color:T.muted }}>{r.email}</div>
                    </div>
                    <div style={{ fontSize:11, color:T.muted, textAlign:"right" }}>
                      <div>{r.department || "—"}</div>
                      <div>{r.designation || "—"}</div>
                    </div>
                  </div>
                ))}
                {validRows.length > 5 && (
                  <div style={{ padding:"10px 16px", fontSize:12, color:T.muted, textAlign:"center" }}>
                    + {validRows.length - 5} more employees
                  </div>
                )}
              </div>

              {importing && (
                <div style={{ marginBottom:20 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, color:T.muted, marginBottom:8 }}>
                    <span>Importing...</span><span>{importProgress}%</span>
                  </div>
                  <div style={{ height:6, borderRadius:3, background:T.border, overflow:"hidden" }}>
                    <div style={{
                      height:"100%", borderRadius:3, transition:"width .3s",
                      background:`linear-gradient(90deg,${T.cyan},${T.purple})`,
                      width:`${importProgress}%`,
                    }}/>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ══ STEP 5: RESULTS ══ */}
          {step === 5 && results && (
            <div style={{ textAlign:"center" }}>
              <div style={{ fontSize:52, marginBottom:16 }}>{results.failed === 0 ? "🎉" : "⚠️"}</div>
              <div style={{ fontSize:20, fontWeight:800, color:T.text, marginBottom:20 }}>
                Import {results.failed === 0 ? "Complete" : "Finished with Errors"}
              </div>
              <div style={{ display:"flex", gap:14, justifyContent:"center", marginBottom:24 }}>
                <div style={{ padding:"16px 28px", borderRadius:12, background:`${T.green}14`, border:`1px solid ${T.green}33` }}>
                  <div style={{ fontSize:32, fontWeight:900, color:T.green }}>{results.ok}</div>
                  <div style={{ fontSize:12, color:T.muted }}>Created Successfully</div>
                </div>
                {results.failed > 0 && (
                  <div style={{ padding:"16px 28px", borderRadius:12, background:`${T.red}14`, border:`1px solid ${T.red}33` }}>
                    <div style={{ fontSize:32, fontWeight:900, color:T.red }}>{results.failed}</div>
                    <div style={{ fontSize:12, color:T.muted }}>Failed</div>
                  </div>
                )}
              </div>
              {results.errors.length > 0 && (
                <div style={{ textAlign:"left", maxHeight:160, overflowY:"auto", borderRadius:10, background:T.card2, border:`1px solid ${T.border}`, padding:"12px 16px" }}>
                  {results.errors.map((e,i) => (
                    <div key={i} style={{ fontSize:12, color:T.red, marginBottom:4 }}>
                      ✗ {e.row}: {e.error}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Footer actions ── */}
        <div style={{
          padding:"16px 28px", borderTop:`1px solid ${T.border}`,
          display:"flex", justifyContent:"space-between", alignItems:"center",
          background:T.card2,
        }}>
          <div>
            {step > 1 && step < 5 && !importing && (
              <button onClick={() => setStep(s => s - 1)} style={{
                padding:"9px 20px", borderRadius:8, border:`1px solid ${T.border}`,
                background:"transparent", color:T.text, cursor:"pointer",
                fontFamily:"inherit", fontWeight:700, fontSize:13,
              }}>← Back</button>
            )}
          </div>
          <div style={{ display:"flex", gap:10 }}>
            {step === 1 && (
              <button onClick={() => fileRef.current.click()} disabled={!!file} style={{
                padding:"9px 22px", borderRadius:8, border:`1px solid ${T.border}`,
                background:"transparent", color:T.text, cursor:"pointer",
                fontFamily:"inherit", fontWeight:700, fontSize:13, opacity:file?0.4:1,
              }}>Upload File</button>
            )}
            {step === 2 && (
              <button onClick={applyMapping} style={{
                padding:"10px 24px", borderRadius:8, border:"none",
                background:`linear-gradient(135deg,${T.cyan},${T.purple})`,
                color:"#fff", cursor:"pointer", fontFamily:"inherit", fontWeight:800, fontSize:14,
              }}>Validate & Continue →</button>
            )}
            {step === 3 && (
              <button onClick={() => setStep(4)} disabled={validRows.length === 0} style={{
                padding:"10px 24px", borderRadius:8, border:"none",
                background:`linear-gradient(135deg,${T.cyan},${T.purple})`,
                color:"#fff", cursor:"pointer", fontFamily:"inherit", fontWeight:800, fontSize:14,
                opacity:validRows.length===0?0.4:1,
              }}>Review & Confirm →</button>
            )}
            {step === 4 && (
              <button onClick={runImport} disabled={importing || validRows.length === 0} style={{
                padding:"10px 28px", borderRadius:8, border:"none",
                background: importing ? T.border : `linear-gradient(135deg,${T.green},${T.cyan})`,
                color:"#fff", cursor: importing?"not-allowed":"pointer",
                fontFamily:"inherit", fontWeight:800, fontSize:14,
              }}>
                {importing ? `Importing... ${importProgress}%` : `🚀 Import ${validRows.length} Employees`}
              </button>
            )}
            {step === 5 && (
              <button onClick={onClose} style={{
                padding:"10px 28px", borderRadius:8, border:"none",
                background:`linear-gradient(135deg,${T.green},${T.cyan})`,
                color:"#fff", cursor:"pointer", fontFamily:"inherit", fontWeight:800, fontSize:14,
              }}>Done ✓</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}